import { useMemo, useState } from "react";
import campaignIcon from "../../../static/assets/campaign.svg";
import movingBlackIcon from "../../../static/assets/moving_black.svg";

const SEVERITY_ORDER = ["high", "medium", "watch"];

const SEVERITY_STYLES = {
  high: { labelKey: "High impact", className: "bg-[#FFE2E5] text-[#B42318]" },
  medium: {
    labelKey: "Medium impact",
    className: "bg-[#FFF4DE] text-[#8A5A00]",
  },
  watch: { labelKey: "To monitor", className: "bg-[#F2F4F7] text-[#475467]" },
};

const groupRecommendationsBySeverity = (recommendations) =>
  SEVERITY_ORDER.map((severity) => ({
    severity,
    items: recommendations.filter((item) => item.severity === severity),
  })).filter((group) => group.items.length > 0);

const FluxRecommendationBlock = ({ recommendation, t }) => {
  const fluxLabel = recommendation.titleParams?.flux;

  return (
    <div className="flex flex-col gap-2 pt-2 first:pt-0 border-t border-[#F0F0F0] first:border-t-0">
      {fluxLabel ? (
        <span className="font-semibold text-sm text-[#000000]">
          {fluxLabel}
        </span>
      ) : null}

      <div className="flex flex-col gap-1">
        <div className="flex flex-row items-center gap-2">
          <img src={campaignIcon} alt="" width={14} height={11} />
          <span className="font-semibold text-sm text-[#000000]">
            {t("Recommendation")}
          </span>
        </div>
        <span className="text-sm text-gray-700">
          {t(recommendation.titleKey, recommendation.titleParams)}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex flex-row items-center gap-2">
          <img src={movingBlackIcon} alt="" width={14} height={8} />
          <span className="font-semibold text-sm text-[#000000]">
            {t("Impact")}
          </span>
        </div>
        <span className="text-sm text-gray-700">
          {t(recommendation.impactKey, recommendation.impactParams)}
        </span>
      </div>
    </div>
  );
};

const SeverityGroupCard = ({ severity, items, t }) => {
  const style = SEVERITY_STYLES[severity] ?? SEVERITY_STYLES.watch;

  return (
    <div className="flex flex-col gap-3 border border-[#E5E5E5] rounded-lg p-4 shrink-0">
      <span
        className={`self-start px-3 py-1 rounded-full text-sm font-semibold ${style.className}`}
      >
        {t(style.labelKey)}
      </span>

      <div className="flex flex-col gap-3">
        {items.map((recommendation) => (
          <FluxRecommendationBlock
            key={recommendation.id}
            recommendation={recommendation}
            t={t}
          />
        ))}
      </div>
    </div>
  );
};

const AutoCollectionHint = ({ t }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-[#D6E4FF] bg-[#F5F8FF] rounded-lg">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full flex flex-row items-center justify-between gap-2 px-3 py-2 text-left"
      >
        <span className="text-sm font-medium text-[#3538CD]">
          {t("recommendation.autoCollectionHintTitle")}
        </span>
        <i
          className={`fas fa-chevron-${isOpen ? "up" : "down"} text-xs text-[#3538CD]`}
          aria-hidden
        />
      </button>
      {isOpen && (
        <div className="px-3 pb-3 text-sm text-[#3538CD] flex flex-col gap-2">
          <span>{t("recommendation.autoCollectionHintBody")}</span>
          <a
            href="mailto:client@akanthas.com"
            className="self-start font-semibold underline"
          >
            client@akanthas.com
          </a>
        </div>
      )}
    </div>
  );
};

const CollectionRecommendationsSection = ({ recommendations, t }) => {
  const groups = useMemo(
    () => groupRecommendationsBySeverity(recommendations),
    [recommendations],
  );
  const hasInstabilityUpsell = recommendations.some(
    (item) => item.showAutoCollectionUpsell,
  );

  return (
    <article className="statistics-card min-lg:flex-1 min-lg:min-w-0 flex flex-col min-h-0 h-full">
      <div className="flex flex-col min-h-0 flex-1">
        <div className="dashboard-chart-heading shrink-0">
          <h5 className="dashboard-chart-title">{t("Recommendations")}</h5>
          <p className="dashboard-chart-subtitle">
            {t("Prioritized actions to optimize your rotations")}
          </p>
        </div>

        {groups.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center text-[#5e5e5e] flex-1 gap-2">
            <span className="fas fa-check-circle text-xl"></span>
            <span>{t("No recommendation for this period")}</span>
          </div>
        ) : (
          <div className="flex flex-col gap-3 min-h-0 flex-1 overflow-y-auto pr-1">
            {groups.map((group) => (
              <SeverityGroupCard
                key={group.severity}
                severity={group.severity}
                items={group.items}
                t={t}
              />
            ))}
            {hasInstabilityUpsell && <AutoCollectionHint t={t} />}
          </div>
        )}
      </div>
    </article>
  );
};

export default CollectionRecommendationsSection;
